/**
 * The creator-row invariant — contract §1.2 as amended by LEAD RULING R2 / G-2.
 *
 * A creator is a `provider_profiles` row with `business_type = 'influencer'`, and
 * it must carry `saoirse_excluded = true` and NULL `latitude`/`longitude`. If it
 * does not, the Saoirse selection can treat a person as a place: a traveller is
 * routed to a creator's pin and told to eat there.
 *
 * WHY THIS IS A CONSOLE CHECK AND NOT A CONSTRAINT. R2/G-2(b) defers the DB-level
 * guarantee to a WP1 HELD migration, because `provider_profiles` is outside WP0's
 * collision map and the trigger's interplay with `protect_saoirse_dial` needs its
 * own proof. Until that lands the invariant is unenforced, and there is a live
 * path that breaks it: the public `#/apply` form still offers `influencer`
 * (`apply.ts:59`), so an approved application can mint a creator row with
 * `saoirse_excluded` defaulting to FALSE and with coordinates the applicant
 * typed. R2/G-2(a) is therefore the interim: the console badges such rows and
 * offers a one-click repair.
 *
 * ⚠️ Note which half actually protects the geo surfaces. Per contract §1.2 it is
 * the NULL coordinates — bbox filters and `if (pLat && pLng)` guards — NOT
 * `saoirse_excluded`. So a row with `saoirse_excluded = true` but real
 * coordinates is still a breach, and this module reports the two independently
 * rather than collapsing them into one boolean.
 *
 * Pure: no DOM, no network, no imports. That is deliberate — `creator-picks-api`
 * cannot be imported under `node:test` (its `supabase` import reads
 * `import.meta.env` at module load and throws), so the predicate R2 asks to pin
 * with a test has to live somewhere a test can reach it.
 */

/** Only the columns the invariant is about; anything wider is accepted. */
export interface CreatorInvariantRow {
  saoirse_excluded?: boolean | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface CreatorInvariantBreach {
  /** `saoirse_excluded <> true` — false OR null, since the column is nullable. */
  notExcluded: boolean;
  /** Either coordinate is set. */
  hasCoordinates: boolean;
  /** One short phrase per broken half, for the badge title. */
  reasons: string[];
}

/** The badge text, per R2/G-2(a). */
export const CREATOR_INVARIANT_BADGE = 'not excluded from tours — fix';

/** Is a coordinate actually set? `0` is a real latitude, not an absence. */
function isSet(value: number | null | undefined): boolean {
  return value !== null && value !== undefined && Number.isFinite(value);
}

/**
 * Report how a creator row breaks the invariant, or `null` when it is clean.
 *
 * `saoirse_excluded` is checked as "not true" rather than "is false" because the
 * live column is nullable with a FALSE default: a row written by any path that
 * does not mention it arrives NULL or false, and both are unprotected.
 */
export function creatorInvariantBreach(row: CreatorInvariantRow): CreatorInvariantBreach | null {
  const notExcluded = row.saoirse_excluded !== true;
  const hasCoordinates = isSet(row.latitude) || isSet(row.longitude);
  if (!notExcluded && !hasCoordinates) return null;

  const reasons: string[] = [];
  if (notExcluded) reasons.push('saoirse_excluded is not true');
  if (hasCoordinates) reasons.push('has map coordinates');
  return { notExcluded, hasCoordinates, reasons };
}

/** Convenience for call sites that only need the yes/no. */
export function creatorNeedsRepair(row: CreatorInvariantRow): boolean {
  return creatorInvariantBreach(row) !== null;
}
